import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Stethoscope, ArrowLeft, Mail, CheckCircle } from 'lucide-react';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/auth/forgot-password', { email });
      setEmailSent(true);
      
      if (response.data.email_sent === false) {
        toast.warning('There was an issue sending the email. Please try again or contact support.');
      } else {
        toast.success('Password reset link sent! Check your inbox and spam folder.');
      }
    } catch (error) {
      // Still show success message for security (don't reveal if email exists)
      setEmailSent(true);
      toast.info('If your email is registered, you will receive a reset link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Stethoscope className="w-10 h-10 text-[#0F766E]" />
            <span className="text-2xl font-bold text-[#0F766E] font-['Manrope']">ClinicalSim AI</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {!emailSent ? (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-teal-100 flex items-center justify-center">
                  <Mail className="w-8 h-8 text-[#0F766E]" />
                </div>
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Forgot Password?</h1>
                <p className="text-slate-500 text-sm">
                  No worries! Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    data-testid="forgot-email-input"
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12"
                  />
                </div>

                <Button 
                  data-testid="send-reset-button"
                  type="submit" 
                  className="w-full h-12 bg-[#0F766E] hover:bg-[#0F766E]/90 text-white"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Check Your Email</h2>
              <p className="text-slate-500 mb-6">
                If an account exists for <strong>{email}</strong>, you will receive a password reset link shortly.
              </p>
              <p className="text-sm text-slate-400 mb-6">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <Button 
                variant="outline" 
                onClick={() => setEmailSent(false)}
                className="mr-2"
              >
                Try Again
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Button
              data-testid="back-to-login"
              variant="ghost"
              onClick={() => navigate('/auth')}
              className="text-[#0F766E] hover:text-[#0F766E]/80"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
